import React, { useState } from 'react';
import {
    Typography,
    Box,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Checkbox,
    ListItemText,
    SelectChangeEvent,
    FormControlLabel,
    Button,
} from '@mui/material';
import Switch from '@mui/material/Switch';
import { useProfileLookupQuery } from '../../../../services/profileService/profile-api';
import { Profile } from '../../../../services/profileService/model/profile/Profile';
import { ApproverSubmitButton } from './ApproverSubmitButton';
import { useDispatch } from 'react-redux';
import { setApproverProfile, toggleFacilityApproverDialog } from '../../../../app/slices/TicketEasyModuleSlice';
import { useCenterListQuery } from '../../../../services/orgService/org-api';
import { TicketApprover } from '../../../../services/ticketEasyService/model/ticketEasy/ticketApprover/TicketApprover';
import { ProfileSearch } from './ProfileSearch';
import { Guid } from 'js-guid';

export function FacilityApproverForm() {
    const dispatch = useDispatch();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
    const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
    const [isPrimary, setIsPrimary] = useState(false);
    const { data: profiles = [] } = useProfileLookupQuery(searchTerm, { skip: !searchTerm });
    const { data: centers = [] } = useCenterListQuery();

    const updateApproverProfile = (updates: Partial<TicketApprover>) => {
        dispatch(setApproverProfile({
            id: new Guid().toString(),
            profileId: selectedProfile?.id || '',
            name: selectedProfile?.fullName || '',
            email: selectedProfile?.email || '',
            upn: selectedProfile?.upn || '',
            locations: selectedLocations,
            approverType: 'Facility',
            isPrimary,
            priorityLevel: updates.priorityLevel ?? 0,
            ...updates,
        }));
    };

    const handleProfileChange = (profile: Profile | null) => {
        setSelectedProfile(profile);
        updateApproverProfile({
            profileId: profile?.id || '',
            name: profile?.fullName || '',
            email: profile?.email || '',
            upn: profile?.upn || '',
        });
    };

    const handleLocationChange = (event: SelectChangeEvent<string[]>) => {
        const locations = event.target.value as string[];
        setSelectedLocations(locations);
        updateApproverProfile({ locations });
    };

    const handlePrimaryToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        const primary = event.target.checked;
        setIsPrimary(primary);
        updateApproverProfile({ isPrimary: primary });
    };

    const handleSubmitSuccess = () => {
        setSelectedProfile(null);
        setSelectedLocations([]);
    };

    return (
        <Box>
            <Typography variant="h5" gutterBottom>
                Set Facility Approver
            </Typography>
            <FormControl fullWidth sx={{ marginBottom: 2 }}>
                <InputLabel id="locations-label">Facilities</InputLabel>
                <Select
                    labelId="locations-label"
                    multiple
                    value={selectedLocations}
                    onChange={handleLocationChange}
                    renderValue={(selected) => selected.join(', ')}
                >
                    {centers.map((center) => (
                        <MenuItem key={center.id} value={center.name}>
                            <Checkbox checked={selectedLocations.indexOf(center.name) > -1} />
                            <ListItemText primary={center.name} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <ProfileSearch
                profiles={profiles}
                selectedProfile={selectedProfile}
                onSearch={setSearchTerm}
                onChange={handleProfileChange}
            />
            <FormControlLabel
                control={
                    <Switch
                        checked={isPrimary}
                        onChange={handlePrimaryToggle}
                        color="primary"
                    />
                }
                label="Primary Approver"
                sx={{ marginBottom: 2 }}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, marginTop: 2 }}>
                <ApproverSubmitButton
                    onSuccess={handleSubmitSuccess}
                />
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => dispatch(toggleFacilityApproverDialog())}
                >
                    Close
                </Button>
            </Box>
        </Box>
    );
}


